import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  TrendingUp, ShoppingBag, MessageCircle, Loader2, 
  DollarSign, Clock, CheckCircle, XCircle, ArrowRight,
  Package, User, LogOut, ChevronRight, Shield
} from 'lucide-react';
import EmailVerificationBanner from '../components/EmailVerificationBanner';
import { DashboardSkeleton } from '../components/PageSkeletons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const userData = localStorage.getItem('user_data');
    if (!userData) {
      navigate('/auth');
      return;
    }
    try { setUser(JSON.parse(userData)); } catch { navigate('/auth'); return; }
    
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/customer/dashboard`);
        if (response.data.success) {
          setDashboard(response.data.dashboard);
        }
      } catch (error) {
        if (error.response?.status === 401) {
          localStorage.removeItem('user_data');
          navigate('/auth');
        } else {
          toast.error('Failed to load dashboard');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  const handleLogout = () => {
    axios.post(`${API}/auth/logout`).catch(() => {});
    localStorage.removeItem('user_data');
    navigate('/');
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      active: 'bg-green-100 text-green-700',
      approved: 'bg-blue-100 text-blue-700',
      completed: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700',
      cancelled: 'bg-gray-100 text-gray-500',
      paid: 'bg-green-100 text-green-700',
      processing: 'bg-blue-100 text-blue-700',
      shipped: 'bg-purple-100 text-purple-700',
      delivered: 'bg-emerald-100 text-emerald-700',
      expired: 'bg-gray-100 text-gray-500',
      open: 'bg-green-100 text-green-700',
      closed: 'bg-gray-100 text-gray-500',
    };
    return styles[status] || 'bg-gray-100 text-gray-600';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-NG', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(amount || 0);
  };

  if (loading) {
    return <DashboardSkeleton />;
  }

  const stats = dashboard?.stats || {};
  const investments = dashboard?.investments || [];
  const orders = dashboard?.orders || [];
  const chats = dashboard?.chats || [];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'investments', label: 'My Investments', icon: TrendingUp, count: investments.length },
    { id: 'orders', label: 'My Orders', icon: ShoppingBag, count: orders.length },
    { id: 'chats', label: 'Support Chats', icon: MessageCircle, count: chats.length },
  ];

  // Determine email verification banner state
  const verificationEmail = dashboard?.profile?.email || user?.email;
  const profileVerifiedAt = dashboard?.profile?.is_email_verified;
  const showVerificationBanner = (
    (dashboard?.profile && !profileVerifiedAt) ||
    (!dashboard?.profile && user?.is_email_verified === false)
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="customer-dashboard">
      {showVerificationBanner && verificationEmail && (
        <EmailVerificationBanner email={verificationEmail} />
      )}
      {/* Header */}
      <div className="bg-gradient-to-r from-green-700 to-emerald-800 text-white">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold" data-testid="dashboard-greeting">
                Welcome, {user?.full_name || 'Customer'}
              </h1>
              <p className="text-green-200 text-sm mt-1">{user?.email}</p>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/investment">
                <Button variant="secondary" size="sm" data-testid="browse-investments-btn">
                  <TrendingUp className="h-4 w-4 mr-1" /> Browse Plans
                </Button>
              </Link>
              <Link to="/account/security">
                <Button variant="ghost" size="sm" className="text-white hover:bg-white/20" data-testid="dashboard-security-btn">
                  <Shield className="h-4 w-4 mr-1" /> Security
                </Button>
              </Link>
              <Button variant="ghost" size="sm" className="text-white hover:bg-white/20" onClick={handleLogout} data-testid="dashboard-logout-btn">
                <LogOut className="h-4 w-4 mr-1" /> Logout
              </Button>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <DollarSign className="h-5 w-5 text-green-300 mb-1" />
              <p className="text-2xl font-bold">{formatCurrency(stats.total_invested)}</p>
              <p className="text-xs text-green-200">Total Invested</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <TrendingUp className="h-5 w-5 text-green-300 mb-1" />
              <p className="text-2xl font-bold">{stats.active_investments || 0}</p>
              <p className="text-xs text-green-200">Active Investments</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <ShoppingBag className="h-5 w-5 text-green-300 mb-1" />
              <p className="text-2xl font-bold">{stats.total_orders || 0}</p>
              <p className="text-xs text-green-200">Total Orders</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <MessageCircle className="h-5 w-5 text-green-300 mb-1" />
              <p className="text-2xl font-bold">{stats.total_chats || 0}</p>
              <p className="text-xs text-green-200">Support Chats</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="max-w-6xl mx-auto px-4 -mt-4">
        <div className="bg-white rounded-xl shadow-sm p-1 flex gap-1 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'bg-green-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  activeTab === tab.id ? 'bg-white/20' : 'bg-gray-200'
                }`}>{tab.count}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Recent Investments */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-600" /> Recent Investments
                </CardTitle>
                {investments.length > 0 && (
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('investments')}>
                    View All <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {investments.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <TrendingUp className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                    <p>No investments yet</p>
                    <Link to="/investment">
                      <Button variant="outline" size="sm" className="mt-3">Explore Investment Plans</Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {investments.slice(0, 3).map((inv) => (
                      <div key={inv.application_number || inv._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{inv.plan_name || 'Investment Plan'}</p>
                          <p className="text-xs text-gray-500">{formatDate(inv.created_at)} | #{inv.application_number}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-sm">{formatCurrency(inv.amount)}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusBadge(inv.status)}`}>
                            {inv.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Orders */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ShoppingBag className="h-5 w-5 text-green-600" /> Recent Orders
                </CardTitle>
                {orders.length > 0 && (
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('orders')}>
                    View All <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {orders.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <ShoppingBag className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                    <p>No orders yet</p>
                    <Link to="/shop">
                      <Button variant="outline" size="sm" className="mt-3">Browse Shop</Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {orders.slice(0, 3).map((order) => (
                      <div key={order.order_number || order._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">Order #{order.order_number}</p>
                          <p className="text-xs text-gray-500">{formatDate(order.created_at)} | {order.items?.length || 0} items</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-sm">{formatCurrency(order.total_amount)}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusBadge(order.status)}`}>
                            {order.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Investments Tab */}
        {activeTab === 'investments' && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">My Investments</CardTitle>
              <Link to="/investment">
                <Button size="sm" className="bg-green-600 hover:bg-green-700">
                  <TrendingUp className="h-4 w-4 mr-1" /> New Investment
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {investments.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <TrendingUp className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-lg font-medium">No investments yet</p>
                  <p className="text-sm mb-4">Start growing your wealth with Daye Farm</p>
                  <Link to="/investment">
                    <Button className="bg-green-600 hover:bg-green-700">View Plans <ArrowRight className="h-4 w-4 ml-1" /></Button>
                  </Link>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-3 font-medium text-gray-500">Application #</th>
                        <th className="pb-3 font-medium text-gray-500">Plan</th>
                        <th className="pb-3 font-medium text-gray-500">Amount</th>
                        <th className="pb-3 font-medium text-gray-500">Return %</th>
                        <th className="pb-3 font-medium text-gray-500">Date</th>
                        <th className="pb-3 font-medium text-gray-500">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {investments.map((inv) => (
                        <tr key={inv.application_number || inv._id} className="border-b last:border-0" data-testid={`investment-row-${inv.application_number}`}>
                          <td className="py-3 font-mono text-xs">{inv.application_number}</td>
                          <td className="py-3">{inv.plan_name || '-'}</td>
                          <td className="py-3 font-semibold">{formatCurrency(inv.amount)}</td>
                          <td className="py-3 text-green-600 font-medium">{inv.return_percentage || '-'}%</td>
                          <td className="py-3 text-gray-500">{formatDate(inv.created_at)}</td>
                          <td className="py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(inv.status)}`}>
                              {inv.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">My Orders</CardTitle>
              <Link to="/shop">
                <Button size="sm" className="bg-green-600 hover:bg-green-700">
                  <ShoppingBag className="h-4 w-4 mr-1" /> Shop Now
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {orders.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <ShoppingBag className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-lg font-medium">No orders yet</p>
                  <p className="text-sm mb-4">Browse our fresh farm products</p>
                  <Link to="/shop">
                    <Button className="bg-green-600 hover:bg-green-700">Browse Shop <ArrowRight className="h-4 w-4 ml-1" /></Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map((order) => (
                    <div key={order.order_number || order._id} className="border rounded-lg p-4" data-testid={`order-row-${order.order_number}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="font-semibold">Order #{order.order_number}</p>
                          <p className="text-xs text-gray-500">{formatDate(order.created_at)}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-lg">{formatCurrency(order.total_amount)}</p>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(order.status)}`}>
                            {order.status}
                          </span>
                        </div>
                      </div>
                      {order.items && (
                        <div className="space-y-1">
                          {order.items.slice(0, 3).map((item, iIdx) => (
                            <div key={item.product_id || item._id || `cd-${order._id}-${iIdx}`} className="flex justify-between text-sm text-gray-600">
                              <span>{item.name || item.product_name} x{item.quantity}</span>
                              <span>{formatCurrency(item.price * item.quantity)}</span>
                            </div>
                          ))}
                          {order.items.length > 3 && (
                            <p className="text-xs text-gray-400">+{order.items.length - 3} more items</p>
                          )}
                        </div>
                      )}
                      {order.payment_method && (
                        <p className="text-xs text-gray-500 mt-2">
                          Payment: {order.payment_method}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Chats Tab */}
        {activeTab === 'chats' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Support Conversations</CardTitle>
            </CardHeader>
            <CardContent>
              {chats.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <MessageCircle className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-lg font-medium">No conversations</p>
                  <p className="text-sm">Use the chat widget to start a conversation</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {chats.map((chat) => (
                    <div key={chat.conversation_id || chat._id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-testid={`chat-row-${chat.conversation_id}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          chat.status === 'open' ? 'bg-green-100' : 'bg-gray-200'
                        }`}>
                          <MessageCircle className={`h-5 w-5 ${chat.status === 'open' ? 'text-green-600' : 'text-gray-400'}`} />
                        </div>
                        <div>
                          <p className="font-medium text-sm">Support Chat</p>
                          <p className="text-xs text-gray-500">{chat.last_message || 'No messages'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(chat.status)}`}>
                          {chat.status}
                        </span>
                        <p className="text-xs text-gray-400 mt-1">{formatDate(chat.updated_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CustomerDashboard;
